import React, { memo } from 'react'
import { useDispatch } from 'react-redux'
import { NlpModel } from '@feedyou/schema'
import { FormValidation } from '@rjsf/utils'
import { useIsStandaloneKbEnabled } from '@features/knowledgeBase'

import { save } from './actions/save'
import { useSettingsPopupState } from '../hooks'
import { SettingsPopup } from '../SettingsPopup'
import { GeneralSection } from './GeneralSection'
import { useFeature } from '../../../store/features'
import { getModelWithUpdatedProviders } from './utils'
import { ModalDescriptorForType } from '../../../store/modals'
import { modalsActions } from '../../../store/modals/modalsSlice'
import { useBotFlow, useNlpModelById } from '../../../store/flow'
import { useSchema } from '../sharedSchemaFragments/nlpSettings/hooks/useSchema'
import { useOpenModalOpenSection } from '../../../store/modals/hooks/useOpenModalOpenSection'
import { useDefaultPromptForProvider } from '../../../features/hostingVersions/hooks/useDefaultPrompts'
import { useUiSchema } from '../sharedSchemaFragments/nlpSettings/hooks/useUiSchema'

type Props = { modalDescriptor: ModalDescriptorForType<'nlp_model_settings'> }

export const NlpModelSettingsPopup: React.FC<Props> = memo(({ modalDescriptor }) => {
  const flow = useBotFlow()
  const schema = useSchema()
  const dispatch = useDispatch()
  const model = useNlpModelById(modalDescriptor.modelId)
  const openSection = useOpenModalOpenSection() ?? 'form'
  const isNlpEntityRepositoryEnabled = !!useFeature('NLP_CUSTOM_ENTITY_OPTION_REPOSITORY')
  const isStandaloneKbEnabled = useIsStandaloneKbEnabled()
  const settingsPopupState = useSettingsPopupState(
    () => getModelWithUpdatedProviders(model!), // getModelWithUpdatedProviders -> convert old schema from version 1.8 to the new one (settings+type -> provider),
    [openSection]
  )

  const allModels = flow?.nlp?.models || []
  const close = () => dispatch(modalsActions.closeModal())
  const nlpModel = settingsPopupState.formData as NlpModel

  const defaultPrompt = useDefaultPromptForProvider(
    nlpModel?.provider?.type,
    nlpModel?.locale,
    nlpModel?.provider?.mode
  )

  const uiSchema = useUiSchema(
    nlpModel?.provider?.type,
    defaultPrompt,
    nlpModel?.provider?.showSources
  )

  return (
    <SettingsPopup
      className="no-error-panel"
      close={close}
      uiSchema={uiSchema.items}
      openSection={openSection}
      formData={settingsPopupState.formData}
      schema={schema.properties.models.items}
      onChange={settingsPopupState.onFormDataChange}
      initialFormData={settingsPopupState.initialFormData}
      customTabNames={{ form: 'Model Settings' }}
      tabOrder={{ form: 0, general: 1, json: 2 }}
      additionalSections={[
        {
          tab: { section: 'general', label: 'General' },
          renderContent: props => <GeneralSection {...props} modelId={modalDescriptor.modelId} />
        }
      ]}
      save={async formData => {
        await dispatch(save({ formData, initialFormData: settingsPopupState.initialFormData }))
        close()
      }}
      validateSchema={(updatedModel: any, errors: FormValidation<any>) => {
        if (
          settingsPopupState.initialFormData.id !== updatedModel.id &&
          allModels.find(({ id }) => id === updatedModel.id)
        ) {
          errors.addError('NLP ID already exists.')
        }

        return errors
      }}
    />
  )
})
